import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Typography, Button, Space, Alert, Spin, Tag, Statistic } from 'antd'
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  ApiOutlined,
  ClockCircleOutlined
} from '@ant-design/icons'
import apiService from '../../services/api'

const { Title, Text } = Typography

const WebhookStatus = () => {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [lastCheck, setLastCheck] = useState(null)
  const [error, setError] = useState(null)

  const checkWebhookStatus = async () => {
    setLoading(true)
    setError(null)
    const startTime = Date.now()

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/webhook/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setStatus({
          success: true,
          message: data.message,
          timestamp: data.timestamp,
          responseTime: Date.now() - startTime
        })
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
    } catch (err) {
      setStatus({
        success: false,
        message: err.message,
        timestamp: new Date().toISOString(),
        responseTime: null
      })
      setError(err.message)
    } finally {
      setLoading(false)
      setLastCheck(new Date())
    }
  }

  useEffect(() => {
    checkWebhookStatus()
  }, [])

  const getStatusColor = () => {
    if (loading) return 'processing'
    return status?.success ? 'success' : 'error'
  }

  const getStatusIcon = () => {
    if (loading) return <Spin size="small" />
    return status?.success ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />
  }

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A'
    return new Date(timestamp).toLocaleString('pt-BR')
  }

  const formatLastCheck = (date) => {
    if (!date) return 'Nunca'
    return date.toLocaleString('pt-BR')
  }

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={2}>
                  <ApiOutlined /> Status do Webhook
                </Title>
                <Text type="secondary">
                  Monitore o status e funcionamento do webhook do sistema
                </Text>
              </div>

              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8}>
                  <Card size="small">
                    <Statistic
                      title="Status Atual"
                      value={loading ? 'Verificando...' : (status?.success ? 'Online' : 'Offline')}
                      prefix={getStatusIcon()}
                      valueStyle={{
                        color: status?.success ? '#52c41a' : '#ff4d4f',
                        fontSize: '18px'
                      }}
                    />
                  </Card>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <Card size="small">
                    <Statistic
                      title="Última Verificação"
                      value={formatLastCheck(lastCheck)}
                      prefix={<ClockCircleOutlined />}
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Card>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <Card size="small">
                    <Statistic
                      title="Endpoint"
                      value="/webhook/test"
                      prefix={<ApiOutlined />}
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Card>
                </Col>
              </Row>

              {status && (
                <Alert
                  message={status.success ? 'Webhook Funcionando' : 'Webhook com Problemas'}
                  description={
                    <div>
                      <p><strong>Mensagem:</strong> {status.message}</p>
                      <p><strong>Timestamp:</strong> {formatTimestamp(status.timestamp)}</p>
                      {status.responseTime && (
                        <p><strong>Tempo de Resposta:</strong> {status.responseTime}ms</p>
                      )}
                    </div>
                  }
                  type={status.success ? 'success' : 'error'}
                  showIcon
                />
              )}

              {error && (
                <Alert
                  message="Erro na Verificação"
                  description={error}
                  type="error"
                  showIcon
                />
              )}

              <div style={{ textAlign: 'center' }}>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  loading={loading}
                  onClick={checkWebhookStatus}
                  size="large"
                >
                  {loading ? 'Verificando...' : 'Verificar Status'}
                </Button>
              </div>

              <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                <Title level={4}>Informações do Webhook</Title>
                <Row gutter={[16, 8]}>
                  <Col span={12}>
                    <Text strong>Endpoint de Teste:</Text>
                  </Col>
                  <Col span={12}>
                    <Tag color="blue">/webhook/test</Tag>
                  </Col>

                  <Col span={12}>
                    <Text strong>Método:</Text>
                  </Col>
                  <Col span={12}>
                    <Tag color="green">POST</Tag>
                  </Col>

                  <Col span={12}>
                    <Text strong>Autenticação:</Text>
                  </Col>
                  <Col span={12}>
                    <Tag color="orange">WebhookAuthGuard</Tag>
                  </Col>

                  <Col span={12}>
                    <Text strong>Resposta Esperada:</Text>
                  </Col>
                  <Col span={12}>
                    <Tag color="purple">HTTP 200 OK</Tag>
                  </Col>
                </Row>
              </Card>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default WebhookStatus